import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cx } from '../lib/cx'
import { Button } from './Button'
import { Icon, type IconName } from './Icon'
import './States.css'

export interface EmptyStateProps {
  /** Decorative icon above the title. */
  icon?: IconName
  title: string
  body?: string
  /** One action, such as a ButtonLink to the next step. */
  action?: ReactNode
  className?: string
}

/** Nothing to show yet, said plainly, with the next step ("All your entries are confirmed"). */
export function EmptyState({ icon = 'inbox', title, body, action, className }: EmptyStateProps) {
  return (
    <section className={cx('state', className)}>
      <span className="state__icon">
        <Icon name={icon} size={32} />
      </span>
      <h2 className="t-heading">{title}</h2>
      {body && <p className="state__body t-muted">{body}</p>}
      {action && <div className="state__action">{action}</div>}
    </section>
  )
}

export interface ErrorStateProps {
  /** Defaults to "Something went wrong". */
  title?: string
  /** Defaults to "Nothing was saved or sent. Try again in a moment." Never blames the person. */
  body?: string
  /** Shows a retry button when given. */
  onRetry?: () => void
  retryLabel?: string
  className?: string
}

/** Something failed: what happened, that nothing was lost, and what to do next. Announced at once. */
export function ErrorState({ title, body, onRetry, retryLabel, className }: ErrorStateProps) {
  const { t } = useTranslation()
  return (
    <section className={cx('state', 'state--error', className)} role="alert">
      <span className="state__icon">
        <Icon name="error" size={32} />
      </span>
      <h2 className="t-heading">{title ?? t('kit.errorState.title')}</h2>
      <p className="state__body t-muted">{body ?? t('kit.errorState.body')}</p>
      {onRetry && (
        <div className="state__action">
          <Button variant="secondary" icon="refresh" onClick={onRetry}>
            {retryLabel ?? t('kit.errorState.retry')}
          </Button>
        </div>
      )}
    </section>
  )
}
